import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { getSwaggerServiceBaseUrl } from "../../../config/apiBase";
import { EmptyState, ErrorState, Skeleton, PageHeader } from "../../../shared-ui/States";
import "../../../shared-ui/crane-tokens.css";

const token = () => localStorage.getItem("zTaskAuthToken") || "";

/**
 * Applications admin — real Java /api/get-app + /api/get-users.
 * Assignment CRUD remains on the auth launcher AssignAppsPage (same APIs).
 */
export default function ApplicationsPage() {
  const [apps, setApps] = useState([]);
  const [users, setUsers] = useState([]);
  const [busy, setBusy] = useState(true);
  const [err, setErr] = useState("");
  const [filter, setFilter] = useState("");

  const load = async () => {
    setBusy(true);
    setErr("");
    const base = getSwaggerServiceBaseUrl();
    const headers = { Authorization: `Bearer ${token()}` };
    try {
      const [appsRes, usersRes] = await Promise.all([
        fetch(`${base}/api/get-app`, { headers }),
        fetch(`${base}/api/get-users`, { headers }),
      ]);
      const appsBody = await appsRes.json().catch(() => ({}));
      const usersBody = await usersRes.json().catch(() => ({}));
      if (!appsRes.ok) throw new Error(appsBody.message || `Apps HTTP ${appsRes.status}`);
      if (!usersRes.ok) throw new Error(usersBody.message || `Users HTTP ${usersRes.status}`);

      const appList =
        appsBody.message ||
        appsBody.data ||
        appsBody.apps ||
        (Array.isArray(appsBody) ? appsBody : []);
      const userList = usersBody.userData || usersBody.message || usersBody.data || [];

      setApps(Array.isArray(appList) ? appList : []);
      setUsers(Array.isArray(userList) ? userList : []);
    } catch (e) {
      setErr(e.message);
      setApps([]);
      setUsers([]);
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const rows = useMemo(() => {
    const q = filter.trim().toLowerCase();
    return (apps || [])
      .map((app) => {
        const id = app.appId ?? app.id;
        const name = app.appName || app.name || `App ${id}`;
        const desc = app.appDescription || app.description || "";
        const assigned = (users || []).filter((u) => {
          const uApps = u.apps || u.appAccess || [];
          if (!Array.isArray(uApps)) return false;
          return uApps.some((a) => {
            if (typeof a === "string") return a.toUpperCase() === String(name).toUpperCase();
            const aid = a.appId ?? a.id;
            const aname = a.appName || a.name;
            return aid === id || String(aname || "").toUpperCase() === String(name).toUpperCase();
          });
        }).length;
        return { id, name, desc, assigned, raw: app };
      })
      .filter((r) => !q || r.name.toLowerCase().includes(q) || r.desc.toLowerCase().includes(q));
  }, [apps, users, filter]);

  const launcherAssignUrl = process.env.REACT_APP_ZTOOL_URL
    ? `${String(process.env.REACT_APP_ZTOOL_URL).replace(/\/$/, "")}/assign-apps`
    : null;

  return (
    <div className="crane-page">
      <PageHeader
        crumbs={["Enterprise Platform", "Administration", "Applications"]}
        title="Applications"
        description="Registered platform applications and assigned-user counts from live Java APIs."
        actions={
          <div style={{ display: "flex", gap: 8 }}>
            <button type="button" className="crane-btn" onClick={load} disabled={busy}>
              Refresh
            </button>
            {launcherAssignUrl ? (
              <a className="crane-btn crane-btn--primary" href={launcherAssignUrl}>
                Manage assignments
              </a>
            ) : (
              <Link className="crane-btn crane-btn--primary" to="/organization/user-management">
                User management
              </Link>
            )}
          </div>
        }
      />

      <div className="crane-filters">
        <input
          className="crane-input"
          style={{ minWidth: 240, flex: 1 }}
          placeholder="Filter applications…"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
        />
      </div>

      {busy && <Skeleton rows={5} />}
      {err && <ErrorState title="Could not load applications" description={err} onRetry={load} />}
      {!busy && !err && rows.length === 0 && (
        <EmptyState title="No applications" description="No applications returned from /api/get-app for this tenant." />
      )}
      {!busy && rows.length > 0 && (
        <div className="crane-card">
          <div className="crane-card__header">
            <h3 className="crane-card__title">Applications status & access</h3>
            <span className="crane-badge">{rows.length} configured</span>
          </div>
          <div style={{ overflowX: "auto" }}>
            <table className="crane-table">
              <thead>
                <tr>
                  <th>Application</th>
                  <th>Code / Id</th>
                  <th>Description</th>
                  <th>Assigned users</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id}>
                    <td style={{ fontWeight: 600 }}>{r.name}</td>
                    <td style={{ fontFamily: "ui-monospace, monospace", fontSize: 12 }}>{r.id}</td>
                    <td>{r.desc || "—"}</td>
                    <td>{r.assigned}</td>
                    <td>
                      <span className="crane-badge crane-badge--ok">
                        <span style={{ width: 6, height: 6, borderRadius: "50%", background: "currentColor", display: "inline-block" }} />
                        Active
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
